'use client';

import { cn } from '@utils/cn';
import { useMissionNavItems } from '@components/features/candidate-prompt-bar/useMissionNavItems';

interface HeaderMissionLinksProps {
  locale: Locale;
  isAuthenticated: boolean;
}

/**
 * Desktop-only flat mission links in the main navbar (Jobs, Coach, Talents)
 * — the desktop counterpart of the mobile BottomNav. Selection comes from
 * useCurrentMission (via useMissionNavItems), shown as the same 3px secondary underline
 * MainNavbarLink uses for active routes.
 */
export const HeaderMissionLinks = ({ locale, isAuthenticated }: HeaderMissionLinksProps) => {
  const { items, handleClick } = useMissionNavItems(locale, isAuthenticated);

  return (
    <div className="hidden lg:flex lg:items-center">
      {items.map(({ item, label }) => (
        <a
          key={item.key}
          href={item.href}
          aria-current={item.selected ? 'page' : undefined}
          onClick={(event) => {
            // Let the browser handle modifier / middle clicks (open in new tab); intercept
            // plain left-clicks for soft-navigation through handleClick.
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            // Tapping the active mission from a sub-page returns to mission home; isOnSamePage
            // (inside handleClick) still prevents same-page churn.
            handleClick({ ...item, selected: false });
          }}
          className={cn(
            'relative flex items-center whitespace-nowrap px-3 pb-4.5 pt-5 text-md font-semibold outline-none transition-colors hover:text-primary xl:px-4',
            "before:absolute before:bottom-0 before:left-0 before:right-0 before:h-[3px] before:content-['']",
            item.selected ? 'text-ink before:bg-secondary' : 'before:bg-transparent',
          )}
        >
          {label}
        </a>
      ))}
    </div>
  );
};
